/**
 * Fleet role editor — load, merge, and persist workforce.roles.
 *
 * JAISIU-4515 §4: models live on the role. The six Bible defaults are
 * always present; configured values overlay them.
 */

import {
  bumpRoleVersion,
  canCreateFleetRole,
  DEFAULT_FLEET_ROLES,
  mergeFleetRoles,
  type FleetRoleAllowedTools,
  type FleetRoleDefinition,
  type FleetRoleInstructionFiles,
} from "../../../../src/config/default-fleet-roles.js";
import { resolveConfiguredModels } from "./agents-list-config-patch.ts";
import { serializeConfigForm } from "./config/form-utils.ts";

export type FleetRolePanel = "overview" | "skills" | "files" | "advanced";

export type FleetRoleDraft = FleetRoleDefinition;

export type FleetRoleFileName = "AGENTS.md" | "SOUL.md" | "TOOLS.md";

export type FleetRoleEditorState = {
  selectedId: string | null;
  panel: FleetRolePanel;
  activeFile: FleetRoleFileName;
  search: string;
  roles: FleetRoleDraft[];
  dirty: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  addOpen: boolean;
  newRoleName: string;
  newRoleDescription: string;
  newRoleSkills: string;
  newSkillName: string;
  newSkillDescription: string;
};

export const FLEET_ROLE_PANELS: Array<{ id: FleetRolePanel; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "skills", label: "Skills" },
  { id: "files", label: "Files" },
  { id: "advanced", label: "Advanced" },
];

export function emptyFleetRoleEditorState(): FleetRoleEditorState {
  return {
    selectedId: DEFAULT_FLEET_ROLES[0]?.roleId ?? null,
    panel: "overview",
    activeFile: "AGENTS.md",
    search: "",
    roles: mergeFleetRoles([]),
    dirty: false,
    loading: false,
    saving: false,
    error: null,
    addOpen: false,
    newRoleName: "",
    newRoleDescription: "",
    newRoleSkills: "",
    newSkillName: "",
    newSkillDescription: "",
  };
}

export function slugifyRoleId(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export function platformDefaultModelLabel(config: Record<string, unknown> | null): string {
  const defaults = (config?.agents as { defaults?: { model?: unknown } } | undefined)?.defaults;
  const raw = defaults?.model;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  if (raw && typeof raw === "object") {
    const primary = (raw as { primary?: unknown }).primary;
    if (typeof primary === "string" && primary.trim()) {
      return primary.trim();
    }
  }
  return "Platform default";
}

export function roleModelLabel(
  role: FleetRoleDraft,
  config: Record<string, unknown> | null,
): string {
  if (!role.model) {
    return `Default model · ${platformDefaultModelLabel(config)}`;
  }
  if (typeof role.model === "string") {
    return role.model;
  }
  return role.model.primary?.trim() || `Default model · ${platformDefaultModelLabel(config)}`;
}

export function roleModelPrimary(role: FleetRoleDraft): string {
  if (!role.model) {
    return "";
  }
  if (typeof role.model === "string") {
    return role.model.trim();
  }
  return role.model.primary?.trim() || "";
}

export function roleModelFallbacks(role: FleetRoleDraft): string[] {
  if (!role.model || typeof role.model === "string") {
    return [];
  }
  return Array.isArray(role.model.fallbacks) ? role.model.fallbacks.filter(Boolean) : [];
}

export function availableRoleModels(config: Record<string, unknown> | null): Array<{
  value: string;
  label: string;
}> {
  const fromDefaults = resolveConfiguredModels(config);
  const fromProviders = modelsFromProviders(config);
  const seen = new Set<string>();
  const out: Array<{ value: string; label: string }> = [
    { value: "", label: `Default model · ${platformDefaultModelLabel(config)}` },
  ];
  for (const option of [...fromDefaults, ...fromProviders]) {
    if (seen.has(option.value)) {
      continue;
    }
    seen.add(option.value);
    out.push(option);
  }
  return out;
}

function modelsFromProviders(config: Record<string, unknown> | null): Array<{
  value: string;
  label: string;
}> {
  const providers = (config?.models as { providers?: Record<string, unknown> } | undefined)
    ?.providers;
  if (!providers || typeof providers !== "object") {
    return [];
  }
  const out: Array<{ value: string; label: string }> = [];
  for (const [providerId, raw] of Object.entries(providers)) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const models = (raw as { models?: unknown }).models;
    if (!Array.isArray(models)) {
      continue;
    }
    for (const model of models) {
      if (!model || typeof model !== "object") {
        continue;
      }
      const id =
        typeof (model as { id?: unknown }).id === "string" ? (model as { id: string }).id : "";
      if (!id.trim()) {
        continue;
      }
      const name =
        typeof (model as { name?: unknown }).name === "string"
          ? (model as { name: string }).name
          : id;
      const value = `${providerId}/${id}`;
      out.push({ value, label: name && name !== id ? `${name} (${value})` : value });
    }
  }
  return out;
}

export function rolesFromConfig(config: Record<string, unknown> | null): FleetRoleDraft[] {
  const workforce = (config?.workforce ?? {}) as { roles?: unknown };
  const raw = Array.isArray(workforce.roles) ? workforce.roles : [];
  const parsed = raw
    .map((entry) => parseConfiguredRole(entry))
    .filter((entry): entry is Partial<FleetRoleDefinition> & { roleId: string } => Boolean(entry));
  return mergeFleetRoles(parsed);
}

function parseConfiguredRole(
  entry: unknown,
): (Partial<FleetRoleDefinition> & { roleId: string }) | null {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const roleId = typeof record.roleId === "string" ? record.roleId.trim() : "";
  if (!roleId) {
    return null;
  }
  const metadata = asRecord(record.metadata);
  const files = parseFiles(metadata.instructionFiles);
  const allowedTools = parseTools(metadata.allowedTools);
  const version =
    typeof metadata.version === "number"
      ? metadata.version
      : typeof record.version === "number"
        ? record.version
        : undefined;
  return {
    roleId,
    name: typeof record.name === "string" ? record.name : undefined,
    description: typeof record.description === "string" ? record.description : undefined,
    skills: Array.isArray(record.skills)
      ? record.skills.filter((skill): skill is string => typeof skill === "string")
      : undefined,
    model: parseModel(record.model),
    priority: typeof record.priority === "number" ? record.priority : undefined,
    version,
    allowedTools,
    files,
  };
}

function parseModel(raw: unknown): string | { primary?: string; fallbacks?: string[] } | undefined {
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  if (raw && typeof raw === "object") {
    const primary =
      typeof (raw as { primary?: unknown }).primary === "string"
        ? (raw as { primary: string }).primary.trim()
        : undefined;
    const fallbacks = Array.isArray((raw as { fallbacks?: unknown }).fallbacks)
      ? ((raw as { fallbacks: unknown[] }).fallbacks.filter(
          (item): item is string => typeof item === "string" && item.trim().length > 0,
        ) as string[])
      : undefined;
    if (primary || (fallbacks && fallbacks.length > 0)) {
      return { ...(primary ? { primary } : {}), ...(fallbacks ? { fallbacks } : {}) };
    }
  }
  return undefined;
}

function parseFiles(raw: unknown): FleetRoleInstructionFiles | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  const out: Partial<FleetRoleInstructionFiles> = {};
  for (const name of ["AGENTS.md", "SOUL.md", "TOOLS.md"] as const) {
    if (typeof record[name] === "string") {
      out[name] = record[name];
    }
  }
  return Object.keys(out).length > 0 ? (out as FleetRoleInstructionFiles) : undefined;
}

function parseTools(raw: unknown): FleetRoleAllowedTools | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  return {
    files: record.files !== false,
    terminal: record.terminal === true,
    git: record.git === true,
    browser: record.browser === true,
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

export function filterFleetRoles(roles: FleetRoleDraft[], search: string): FleetRoleDraft[] {
  const q = search.trim().toLowerCase();
  if (!q) {
    return roles;
  }
  return roles.filter((role) => {
    return (
      role.name.toLowerCase().includes(q) ||
      role.roleId.toLowerCase().includes(q) ||
      role.description.toLowerCase().includes(q)
    );
  });
}

export function selectFleetRole(state: FleetRoleEditorState, roleId: string): FleetRoleEditorState {
  return { ...state, selectedId: roleId, addOpen: false };
}

export function patchSelectedRole(
  state: FleetRoleEditorState,
  patch: Partial<FleetRoleDraft>,
): FleetRoleEditorState {
  const selectedId = state.selectedId;
  if (!selectedId) {
    return state;
  }
  return {
    ...state,
    dirty: true,
    roles: state.roles.map((role) => (role.roleId === selectedId ? { ...role, ...patch } : role)),
  };
}

export function addSkillToSelected(
  state: FleetRoleEditorState,
  skillName: string,
): FleetRoleEditorState {
  const name = skillName.trim();
  if (!name) {
    return state;
  }
  const selected = state.roles.find((role) => role.roleId === state.selectedId);
  if (!selected || selected.skills.includes(name)) {
    return { ...state, newSkillName: "", newSkillDescription: "" };
  }
  return {
    ...patchSelectedRole(state, { skills: [...selected.skills, name] }),
    newSkillName: "",
    newSkillDescription: "",
  };
}

export function removeSkillFromSelected(
  state: FleetRoleEditorState,
  skillName: string,
): FleetRoleEditorState {
  const selected = state.roles.find((role) => role.roleId === state.selectedId);
  if (!selected) {
    return state;
  }
  return patchSelectedRole(state, {
    skills: selected.skills.filter((skill) => skill !== skillName),
  });
}

export function canSubmitNewRole(state: FleetRoleEditorState): boolean {
  return canCreateFleetRole({
    description: state.newRoleDescription,
    skills: state.newRoleSkills
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean),
  });
}

export function appendNewRole(state: FleetRoleEditorState): FleetRoleEditorState {
  const name = state.newRoleName.trim();
  const description = state.newRoleDescription.trim();
  const skills = state.newRoleSkills
    .split(",")
    .map((skill) => skill.trim())
    .filter(Boolean);
  if (!name || !canCreateFleetRole({ description, skills })) {
    return state;
  }
  let roleId = slugifyRoleId(name);
  if (!roleId) {
    roleId = `role-${state.roles.length + 1}`;
  }
  if (state.roles.some((role) => role.roleId === roleId)) {
    roleId = `${roleId}-${state.roles.length + 1}`;
  }
  const next: FleetRoleDraft = {
    roleId,
    name,
    description,
    skills,
    priority: 1,
    version: 1,
    allowedTools: { files: true, terminal: false, git: false, browser: false },
    files: {
      "AGENTS.md": `# ${name}\n\n## Purpose\n${description}\n`,
      "SOUL.md": "",
      "TOOLS.md": "",
    },
    builtIn: false,
  };
  return {
    ...state,
    roles: [...state.roles, next],
    selectedId: roleId,
    panel: "overview",
    dirty: true,
    addOpen: false,
    newRoleName: "",
    newRoleDescription: "",
    newRoleSkills: "",
  };
}

export function serializeFleetRoles(roles: FleetRoleDraft[]): Array<Record<string, unknown>> {
  return roles.map((role) => {
    const model = normalizePersistedModel(role.model);
    return {
      roleId: role.roleId,
      name: role.name,
      description: role.description,
      skills: role.skills,
      priority: role.priority,
      ...(model ? { model } : {}),
      metadata: {
        version: role.version,
        allowedTools: role.allowedTools,
        instructionFiles: role.files,
      },
    };
  });
}

function normalizePersistedModel(
  model: FleetRoleDraft["model"],
): string | { primary?: string; fallbacks?: string[] } | undefined {
  if (!model) {
    return undefined;
  }
  if (typeof model === "string") {
    return model.trim() || undefined;
  }
  const primary = model.primary?.trim();
  const fallbacks = (model.fallbacks ?? []).map((item) => item.trim()).filter(Boolean);
  if (!primary && fallbacks.length === 0) {
    return undefined;
  }
  return {
    ...(primary ? { primary } : {}),
    ...(fallbacks.length > 0 ? { fallbacks } : {}),
  };
}

type ConfigClient = {
  request: (method: string, params: Record<string, unknown>) => Promise<unknown>;
};

export type FleetRolesHost = {
  client: ConfigClient | null;
  connected: boolean;
  configForm: Record<string, unknown> | null;
  fleetRoleEditor: FleetRoleEditorState;
};

export function hydrateFleetRolesFromConfig(host: FleetRolesHost): void {
  const roles = rolesFromConfig(host.configForm);
  const selectedStillThere = roles.some((role) => role.roleId === host.fleetRoleEditor.selectedId);
  host.fleetRoleEditor = {
    ...host.fleetRoleEditor,
    roles,
    selectedId: selectedStillThere ? host.fleetRoleEditor.selectedId : (roles[0]?.roleId ?? null),
    dirty: false,
    error: null,
  };
}

export async function saveFleetRoles(host: FleetRolesHost): Promise<boolean> {
  if (!host.client || !host.connected || host.fleetRoleEditor.saving) {
    return false;
  }
  host.fleetRoleEditor = { ...host.fleetRoleEditor, saving: true, error: null };
  try {
    const snapshot = (await host.client.request("config.get", {})) as {
      hash?: string;
      config?: Record<string, unknown>;
    };
    const baseHash = snapshot.hash;
    const current = snapshot.config
      ? (JSON.parse(JSON.stringify(snapshot.config)) as Record<string, unknown>)
      : null;
    if (!baseHash || !current) {
      throw new Error("Config hash missing; reload and retry.");
    }
    const bumped = host.fleetRoleEditor.roles.map((role) => ({
      ...role,
      version: host.fleetRoleEditor.dirty ? bumpRoleVersion(role.version) : role.version,
    }));
    const workforce = {
      ...asRecord(current.workforce),
      enabled: (current.workforce as { enabled?: boolean } | undefined)?.enabled !== false,
      roles: serializeFleetRoles(bumped),
    };
    current.workforce = workforce;
    await host.client.request("config.set", {
      raw: serializeConfigForm(current),
      baseHash,
    });
    host.fleetRoleEditor = {
      ...host.fleetRoleEditor,
      roles: bumped,
      dirty: false,
      saving: false,
    };
    return true;
  } catch (err) {
    host.fleetRoleEditor = {
      ...host.fleetRoleEditor,
      saving: false,
      error: String(err),
    };
    return false;
  }
}

export { canCreateFleetRole };
